import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { PROFILE } from "@/constants/testIds";
import { toast } from "sonner";
import ShareSoulMark from "@/components/ShareSoulMark";

const ELEMENT_GLYPH = { Fire: "△", Water: "▽", Air: "△̸", Earth: "▽̸" };

export default function Profile() {
  const [data, setData] = useState(null);
  const [affirmation, setAffirmation] = useState(null);
  const [editing, setEditing] = useState(false);
  const [soulName, setSoulName] = useState("");
  const [saving, setSaving] = useState(false);
  const [mark, setMark] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/profile/me");
      setData(d);
      setSoulName(d?.soul_record?.profile?.soul_name || "");
    } catch (e) {
      toast.error("Could not load your soul record.");
    }
  };

  useEffect(() => {
    load();
    api.get("/profile/affirmation").then((r) => setAffirmation(r.data.affirmation)).catch(() => {});
    api.get("/deck/soul-mark").then((r) => setMark(r.data)).catch(() => {});
  }, []);

  const saveName = async () => {
    setSaving(true);
    try {
      await api.post("/profile/update", { soul_name: soulName });
      toast.success("Your soul name is rewritten.");
      setEditing(false);
      load();
    } catch (e) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="font-overline breathe">Attuning…</div>
      </div>
    );
  }

  const soul = data.soul_record;
  const p = soul?.profile;
  const counts = data.counts;

  return (
    <div
      data-testid={PROFILE.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="starfield-subtle" />
      <div className="relative max-w-5xl mx-auto">
        <div className="mb-14">
          <div className="font-overline mb-4">Profile · Soul Record</div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              {!editing ? (
                <div className="flex items-baseline gap-4">
                  <h1
                    data-testid={PROFILE.soulName}
                    className="font-heading italic text-5xl sm:text-6xl lg:text-7xl text-[#F4EFE6] font-light"
                  >
                    {p?.soul_name || data.user.name}
                  </h1>
                  <button
                    data-testid={PROFILE.editSoulNameBtn}
                    className="font-overline text-[#F4EFE6]/40 hover:text-[#C8A96E]"
                    onClick={() => setEditing(true)}
                  >
                    edit
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <input
                    data-testid={PROFILE.soulNameInput}
                    className="input-sacred text-4xl font-heading italic"
                    value={soulName}
                    onChange={(e) => setSoulName(e.target.value)}
                    placeholder="soul name"
                  />
                  <button
                    data-testid={PROFILE.saveSoulNameBtn}
                    className="btn-sacred"
                    onClick={saveName}
                    disabled={saving}
                  >
                    {saving ? "…" : "Save"}
                  </button>
                  <button className="font-overline text-[#F4EFE6]/40" onClick={() => setEditing(false)}>
                    cancel
                  </button>
                </div>
              )}
              <div className="mt-3 font-overline text-[#F4EFE6]/40">
                Given name · {p?.birth_name || data.user.name}
              </div>
            </div>
            {data.deck.owned && (
              <div className="flex items-center gap-4">
                <div className="w-24 aspect-square card-frame p-1 overflow-hidden">
                  {data.deck.grid_url && (
                    <img
                      src={mediaUrl(data.deck.grid_url)}
                      alt="Deck grid"
                      className="w-full h-full object-cover rounded-sm"
                    />
                  )}
                </div>
                <div>
                  <div className="font-overline text-[#C8A96E]">Deck owned</div>
                  <div className="font-heading text-xl italic text-[#F4EFE6]">{data.deck.element} current</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {affirmation && (
          <motion.div
            data-testid={PROFILE.affirmation}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4 }}
            className="mb-20 pl-6 border-l-2 border-[#C8A96E]/40"
          >
            <div className="font-overline mb-2">Your affirmation</div>
            <p className="font-heading italic text-2xl sm:text-3xl text-[#F4EFE6]/95 leading-snug">
              {affirmation}
            </p>
          </motion.div>
        )}

        {mark && (
          <div className="mb-20 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <img
              src={mediaUrl(mark.image_url)}
              alt="Your Soul Mark"
              className="w-28 h-28 rounded-xl object-cover"
              style={{
                border: "1px solid rgba(212,175,55,0.4)",
                boxShadow: "0 0 24px rgba(212,175,55,0.18)",
              }}
            />
            <div>
              <div className="font-overline mb-2">Your Soul Mark</div>
              <p className="font-heading italic text-lg text-[#F4EFE6]/75 leading-snug mb-4 max-w-md">
                Carry it with you. Send it to someone who would understand.
              </p>
              <ShareSoulMark
                imageUrl={mediaUrl(mark.image_url)}
                title={soul?.character_reveal?.archetype_title || ""}
                soulName={p?.soul_name || data.user.name}
                sentence={
                  soul?.character_reveal?.body ||
                  soul?.character_reveal?.blessing ||
                  affirmation ||
                  ""
                }
              />
            </div>
          </div>
        )}

        {/* Natal + Numerology grid */}
        {p && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
            <section>
              <div className="font-overline mb-6">Natal signature</div>
              <div className="grid grid-cols-3 gap-3">
                <NatalCell testId={PROFILE.natalCard("sun")} label="Sun" value={p.astrology.sun} sub={p.astrology.sun_element} />
                <NatalCell testId={PROFILE.natalCard("moon")} label="Moon" value={p.astrology.moon} />
                <NatalCell testId={PROFILE.natalCard("rising")} label="Rising" value={p.astrology.rising} />
              </div>
              <div className="mt-6 card-frame p-6">
                <div className="font-overline mb-3">Elemental scores</div>
                <div className="space-y-3">
                  {["Fire", "Water", "Air", "Earth"].map((el) => {
                    const score = p.elemental.scores[el] || 0;
                    const max = Math.max(...Object.values(p.elemental.scores), 1);
                    const pct = Math.round((score / max) * 100);
                    return (
                      <div key={el}>
                        <div className="flex items-center justify-between font-overline mb-1">
                          <span>
                            <span className="text-[#C8A96E] mr-2">{ELEMENT_GLYPH[el]}</span>
                            {el}
                          </span>
                          <span className="text-[#F4EFE6]/50">{score}</span>
                        </div>
                        <div className="h-px bg-[#F4EFE6]/10">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 1.6, ease: "easeInOut" }}
                            className={`h-[3px] ${el === p.elemental.dominant ? "bg-[#C8A96E]" : "bg-[#F4EFE6]/40"}`}
                            style={{ transform: "translateY(-1px)" }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <section>
              <div className="font-overline mb-6">Numerology</div>
              <div className="space-y-4">
                <NumerologyCard
                  testId={PROFILE.numerologyCard("life_path")}
                  label="Life Path"
                  number={p.numerology.life_path}
                  desc={p.numerology.life_path_archetype}
                />
                <div className="grid grid-cols-2 gap-4">
                  <NumerologyCard testId={PROFILE.numerologyCard("expression")} label="Expression" number={p.numerology.expression} />
                  <NumerologyCard testId={PROFILE.numerologyCard("soul_urge")} label="Soul Urge" number={p.numerology.soul_urge} />
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CountCard testId={PROFILE.countCard("readings")} label="Readings" value={counts.readings} />
          <CountCard testId={PROFILE.countCard("journal")} label="Journal entries" value={counts.journal} />
          <CountCard testId={PROFILE.countCard("bookmarks")} label="Bookmarks" value={counts.bookmarks} />
          <CountCard testId={PROFILE.countCard("orders")} label="Deck orders" value={counts.orders} />
        </div>
      </div>
    </div>
  );
}

function NatalCell({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="card-frame surface-grain p-4">
      <div className="font-overline mb-1">{label}</div>
      <div className="font-heading italic text-2xl text-[#F4EFE6]">{value}</div>
      {sub && <div className="font-overline text-[#C8A96E]/70 mt-1">{sub}</div>}
    </div>
  );
}

function NumerologyCard({ label, number, desc, testId }) {
  return (
    <div data-testid={testId} className="card-frame surface-grain p-6">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-overline">{label}</div>
        <div className="font-heading text-4xl text-[#C8A96E] font-light">{number}</div>
      </div>
      {desc && <div className="font-heading italic text-lg text-[#F4EFE6]/85 mt-2 leading-snug">{desc}</div>}
    </div>
  );
}

function CountCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="card-frame surface-grain p-4 text-center">
      <div className="font-heading text-3xl text-[#F4EFE6] font-light mb-1">{value}</div>
      <div className="font-overline text-[#F4EFE6]/50">{label}</div>
    </div>
  );
}
